import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

import { ToastMain, SweetAlertShow, LoadingDiv } from "../master/Master";
import { BankList } from "../../api/BankService";
import { getSessions } from "../../api/SessionsService";
import { getBatchList } from "../../api/BatchService";
import { GetCreditDebit } from "../../api/HostelService";
import HostelHeader from "./HostelHeader";
import Nav from "../master/NavBar";
import Footer from "../main/footer";
import WelcomeBar from "./WelcomeBar";
import { StaffList } from "../../api/Staff";
import usePrivateLink from "../config/usePrivateLink";
import HostelReportTabs from "./HostelReportTabs";

export default function HotelCreditDebit() {
  const hasAccess = usePrivateLink();

  /* -------------------------------------------------------------------------- */
  /*                                   STATE                                    */
  /* -------------------------------------------------------------------------- */

  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [nav, setNav] = useState({});
  const [batches, setBatches] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [banks, setBanks] = useState([]);

  const [selectedSession, setSelectedSession] = useState("");
  const [batchId, setBatchId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [type, setType] = useState("all");
  const [name, setName] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const hostelId = localStorage.getItem("hostel_id");

  /* -------------------------------------------------------------------------- */
  /*                              DEFAULT DATES                                 */
  /* -------------------------------------------------------------------------- */

  const getDefaultDates = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDate(firstDay),
      to: formatDate(today),
    };
  };

  const { from: defaultFrom, to: defaultTo } = getDefaultDates();

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  /* -------------------------------------------------------------------------- */
  /*                              FETCH FUNCTIONS                               */
  /* -------------------------------------------------------------------------- */

  const getBusinessBanks = async () => {
    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(response?.data?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to fetch accounts.",
        );
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message || "Something went wrong.",
      );
    }
  };

  const getSession = async () => {
    try {
      const response = await getSessions();

      if (response?.data?.status) {
        const sessionList = response?.data?.data || [];

        setSessions(sessionList);

        const activeSession = sessionList.find(
          (session) => String(session.status) === "1",
        );

        if (activeSession) {
          setSelectedSession(String(activeSession.id));
        } else if (sessionList.length > 0) {
          setSelectedSession(String(sessionList[0].id));
        }
      } else {
        setError("Failed to fetch sessions.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to fetch sessions.");
    }
  };

  const getBatches = async () => {
    if (!selectedSession) return;

    try {
      const response = await getBatchList(`&session_id=${selectedSession}`);

      setBatches(response?.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch batches:", err);
    }
  };

  const getStaffs = async () => {
    try {
      const response = await StaffList();

      setStaffs(response?.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch staff:", err);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               QUERY BUILDER                                */
  /* -------------------------------------------------------------------------- */

  const buildQuery = (currentPage = page) => {
    let query = `&page=${currentPage}`;

    if (selectedSession) query += `&session_id=${selectedSession}`;
    if (from) query += `&start_date=${from}`;
    if (to) query += `&end_date=${to}`;
    // if (batchId) query += `&batch_id=${batchId}`;
    if (staffId) query += `&staff_id=${staffId}`;
    if (name.trim()) query += `&search=${encodeURIComponent(name.trim())}`;

    if (type && type !== "all") {
      query += `&type=${type}`;
    }

    if (selectedAccount) {
      query += `&bank_id=${selectedAccount}`;
    }

    if (hostelId) {
      query += `&hostel_id=${hostelId}`;
    }

    return query;
  };

  /* -------------------------------------------------------------------------- */
  /*                              FETCH REPORT                                  */
  /* -------------------------------------------------------------------------- */

  const fetchData = async () => {
    if (!selectedSession) return;

    setLoading(true);

    try {
      const response = await GetCreditDebit(buildQuery(page));

      const responseData = response?.data;

      setData(Array.isArray(responseData?.data) ? responseData.data : []);

      setNav(responseData || {});
    } catch (err) {
      console.error("Failed to fetch credit/debit data:", err);

      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ||
          "Unable to load credit/debit transactions.",
      );

      setData([]);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                  EXPORT                                    */
  /* -------------------------------------------------------------------------- */

  const handleExportAll = async () => {
    try {
      setLoading(true);

      const baseQuery = buildQuery(1).replace("&page=1", "");

      const firstResponse = await GetCreditDebit(`${baseQuery}&page=1`);

      const apiData = firstResponse?.data;

      let allData = Array.isArray(apiData?.data) ? [...apiData.data] : [];

      const lastPage = Number(apiData?.last_page || 1);

      if (!allData.length) {
        ToastMain.fire({
          icon: "info",
          title: "No data found for selected filters!",
        });

        return;
      }

      for (let currentPage = 2; currentPage <= lastPage; currentPage++) {
        const response = await GetCreditDebit(
          `${baseQuery}&page=${currentPage}`,
        );

        const records = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        allData.push(...records);
      }

      const formattedData = allData.map((item) => {
        const staff = item?.staff_belongs || {};
        const bank = item?.bank || {};

        return {
          "Transaction ID": item?.id || "",
          Date: item?.created_at
            ? new Date(item.created_at).toLocaleDateString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "",
          "Staff Name": staff?.name || "",
          "Staff ID": staff?.staff_id || "",
          Phone: staff?.phone || "",
          Village: staff?.village || "",
          District: staff?.district || "",
          State: staff?.state || "",
          Amount: Number(item?.amount || 0),
          Type: item?.type || "",
          "Payment Method": item?.payment_method || "-",
          "Transaction ID / Ref": item?.trans_id || "-",
          Bank: bank?.owner_name || "",
          "Account No": bank?.account_no || "",
          Branch: bank?.branch || "",
          Remark: item?.note || "-",
        };
      });

      const workbook = XLSX.utils.book_new();

      const totalCredit = Number(apiData?.total_credit || 0);
      const totalDebit = Number(apiData?.total_debit || 0);

      const summaryData = [
        {
          "Total Credit": totalCredit,
          "Total Debit": totalDebit,
          "Net Balance": totalCredit - totalDebit,
          "Records Exported": allData.length,
          "From Date": from || "-",
          "To Date": to || "-",
        },
      ];

      const summarySheet = XLSX.utils.json_to_sheet(summaryData);

      summarySheet["!cols"] = [
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 15 },
        { wch: 15 },
      ];

      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

      const detailSheet = XLSX.utils.json_to_sheet(formattedData);

      detailSheet["!cols"] = [
        { wch: 15 },
        { wch: 15 },
        { wch: 25 },
        { wch: 15 },
        { wch: 15 },
        { wch: 18 },
        { wch: 18 },
        { wch: 15 },
        { wch: 14 },
        { wch: 18 },
        { wch: 25 },
        { wch: 25 },
        { wch: 20 },
        { wch: 20 },
        { wch: 35 },
      ];

      XLSX.utils.book_append_sheet(workbook, detailSheet, "Transactions");

      XLSX.writeFile(workbook, `CreditDebitReport_${from}_${to}.xlsx`);

      ToastMain.fire({
        icon: "success",
        title: `Exported ${allData.length} records successfully!`,
      });
    } catch (err) {
      console.error("Export Error:", err);

      ToastMain.fire({
        icon: "error",
        title: err?.message || "Error exporting data!",
      });
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                KPI VALUES                                  */
  /* -------------------------------------------------------------------------- */

  const totalCredit = Number(nav?.total_credit || 0);
  const totalDebit = Number(nav?.total_debit || 0);
  const netBalance = totalCredit - totalDebit;

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;

  const formatDate = (value) => {
    if (!value) return "-";

    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  /* -------------------------------------------------------------------------- */
  /*                               PAGINATION                                   */
  /* -------------------------------------------------------------------------- */

  const paginationPages = useMemo(() => {
    const lastPage = Number(nav?.last_page || 1);
    const currentPage = Number(nav?.current_page || page);

    if (lastPage <= 7) {
      return Array.from({ length: lastPage }, (_, i) => i + 1);
    }

    const pages = new Set([
      1,
      lastPage,
      currentPage,
      currentPage - 1,
      currentPage + 1,
    ]);

    return [...pages]
      .filter((item) => item >= 1 && item <= lastPage)
      .sort((a, b) => a - b);
  }, [nav, page]);

  /* -------------------------------------------------------------------------- */
  /*                                  EFFECTS                                   */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    getBusinessBanks();
    getSession();
    getStaffs();
  }, []);

  // useEffect(() => {
  //   getBatches();

  //   setBatchId("");
  // }, [selectedSession]);

  useEffect(() => {
    setPage(1);
  }, [from, to, batchId, staffId, selectedSession, type, selectedAccount]);

  useEffect(() => {
    fetchData();
  }, [
    page,
    from,
    to,
    batchId,
    staffId,
    selectedSession,
    type,
    selectedAccount,
    name,
  ]);

  /* -------------------------------------------------------------------------- */
  /*                                   UI                                       */
  /* -------------------------------------------------------------------------- */

  return (
    <>
      {/* Loading Overlay */}
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <HostelHeader />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                {/* <WelcomeBar /> */}

                {/* ---------------------------------------------------------- */}
                {/* REPORT HEADER                                              */}
                {/* ---------------------------------------------------------- */}

                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className="avatar avatar-sm rounded bg-label-primary">
                        <i className="ri ri-exchange-funds-line ri-20px" />
                      </span>

                      <h4 className="mb-0">Credit & Debit Report</h4>
                    </div>

                    <p className="text-muted mb-0">
                      Track hostel income, expenses and account transactions.
                    </p>
                  </div>

                  <div
                    className="form-floating form-floating-outline"
                    style={{ minWidth: 220 }}
                  >
                    <select
                      className="form-select"
                      value={selectedSession}
                      onChange={(e) => setSelectedSession(e.target.value)}
                    >
                      <option value="">Select Session</option>

                      {sessions?.map((session) => (
                        <option key={session?.id} value={session?.id}>
                          {session?.session}
                          {String(session?.status) === "1"
                            ? " — Active"
                            : " — Inactive"}
                        </option>
                      ))}
                    </select>

                    <label>Academic Session</label>
                  </div>
                </div>

                {/* ---------------------------------------------------------- */}
                {/* REPORT NAVIGATION                                           */}
                {/* ---------------------------------------------------------- */}

                <HostelReportTabs />

                {/* ---------------------------------------------------------- */}
                {/* KPI CARDS                                                   */}
                {/* ---------------------------------------------------------- */}

                <div className="row g-3 mb-4">
                  {/* Credit */}
                  <div className="col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <span className="text-muted small">
                              Total Credit
                            </span>

                            <h4 className="mb-1 mt-1 text-success">
                              {formatCurrency(totalCredit)}
                            </h4>

                            <small className="text-muted">
                              Fee / income received
                            </small>
                          </div>

                          <div className="avatar rounded bg-label-success">
                            <i className="ri ri-arrow-down-circle-line ri-24px" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Debit */}
                  <div className="col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <span className="text-muted small">
                              Total Debit
                            </span>

                            <h4 className="mb-1 mt-1 text-danger">
                              {formatCurrency(totalDebit)}
                            </h4>

                            <small className="text-muted">
                              Expenses / payments
                            </small>
                          </div>

                          <div className="avatar rounded bg-label-danger">
                            <i className="ri ri-arrow-up-circle-line ri-24px" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Balance */}
                  <div className="col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <span className="text-muted small">
                              Net Balance
                            </span>

                            <h4
                              className={`mb-1 mt-1 ${
                                netBalance >= 0 ? "text-primary" : "text-danger"
                              }`}
                            >
                              {formatCurrency(netBalance)}
                            </h4>

                            <small className="text-muted">Credit − Debit</small>
                          </div>

                          <div className="avatar rounded bg-label-primary">
                            <i className="ri ri-wallet-3-line ri-24px" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ---------------------------------------------------------- */}
                {/* FILTER CARD                                                 */}
                {/* ---------------------------------------------------------- */}

                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header border-bottom">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h5 className="mb-1">
                          <i className="ri ri-filter-3-line me-2 text-primary" />
                          Report Filters
                        </h5>

                        <small className="text-muted">
                          Refine transactions by date, type or account.
                        </small>
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          setFrom(defaultFrom);
                          setTo(defaultTo);
                          // setBatchId("");
                          setStaffId("");
                          setSelectedAccount("");
                          setType("all");
                          setName("");
                          setPage(1);
                        }}
                      >
                        <i className="ri ri-refresh-line me-1" />
                        Reset
                      </button>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      {/* Search */}
                      <div className="col-lg-4">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                          />

                          <label>
                            <i className="ri ri-search-line me-1" />
                            Search Transaction / Staff
                          </label>
                        </div>
                      </div>

                      {/* From */}
                      <div className="col-sm-6 col-lg-2">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="date"
                            className="form-control"
                            value={from}
                            onChange={(e) => setFrom(e.target.value)}
                          />

                          <label>From Date</label>
                        </div>
                      </div>

                      {/* To */}
                      <div className="col-sm-6 col-lg-2">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="date"
                            className="form-control"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                          />

                          <label>To Date</label>
                        </div>
                      </div>

                      {/* Type */}
                      <div className="col-sm-6 col-lg-2">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                          >
                            <option value="all">All Transactions</option>

                            <option value="fee">Credit / Fee</option>

                            <option value="expense">Debit / Expense</option>
                          </select>

                          <label>Transaction Type</label>
                        </div>
                      </div>

                      {/* Account */}
                      <div className="col-sm-6 col-lg-2">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            value={selectedAccount}
                            onChange={(e) => setSelectedAccount(e.target.value)}
                          >
                            <option value="">All Accounts</option>

                            {banks?.map((item) => (
                              <option key={item?.id} value={item?.id}>
                                {item?.account_no} — {item?.owner_name}
                              </option>
                            ))}
                          </select>

                          <label>Bank Account</label>
                        </div>
                      </div>

                      {/* Batch */}
                      {/* <div className="col-md-4">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            value={batchId}
                            onChange={(e) => setBatchId(e.target.value)}
                          >
                            <option value="">All Batches</option>

                            {batches?.map((batch) => (
                              <option key={batch?.id} value={batch?.id}>
                                {batch?.name ||
                                  batch?.batch_name ||
                                  batch?.batch}
                              </option>
                            ))}
                          </select>

                          <label>Batch</label>
                        </div>
                      </div> */}

                      {/* Staff */}
                      <div className="col-md-4">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            value={staffId}
                            onChange={(e) => setStaffId(e.target.value)}
                          >
                            <option value="">All Staff</option>

                            {staffs?.map((staff) => (
                              <option key={staff?.id} value={staff?.id}>
                                {staff?.name ||
                                  staff?.staff_name ||
                                  staff?.staff_id}
                              </option>
                            ))}
                          </select>

                          <label>Staff</label>
                        </div>
                      </div>

                      {/* Active Filters */}
                      <div className="col-md-4 d-flex align-items-center">
                        <div className="d-flex flex-wrap gap-2">
                          {type !== "all" && (
                            <span className="badge bg-label-warning">
                              {type === "fee" ? "Credit" : "Debit"}
                            </span>
                          )}

                          {selectedAccount && (
                            <span className="badge bg-label-info">
                              Account selected
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ---------------------------------------------------------- */}
                {/* TRANSACTION TABLE                                           */}
                {/* ---------------------------------------------------------- */}

                <div className="card border-0 shadow-sm">
                  <div className="card-header">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <h5 className="mb-0">Transactions</h5>

                          <span className="badge bg-label-primary">
                            {nav?.total || 0}
                          </span>
                        </div>

                        <small className="text-muted">
                          Showing filtered credit and debit entries
                        </small>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <div className="text-end d-none d-md-block">
                          <div className="small text-muted">Period</div>

                          <div className="fw-semibold">
                            {from ? formatDate(from) : "-"} →{" "}
                            {to ? formatDate(to) : "-"}
                          </div>
                        </div>

                        {hasAccess("/hostel/credit/debit/report/export") && (
                          <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleExportAll}
                            disabled={loading}
                          >
                            <i className="ri ri-file-excel-2-line me-1" />
                            <span className="d-none d-sm-inline">
                              Export Excel
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th className="ps-4">#</th>
                          <th>Date</th>
                          <th>Payment</th>
                          <th>Bank Account</th>
                          <th>Remark</th>
                          <th className="text-end text-success">Credit</th>
                          <th className="text-end text-danger pe-4">Debit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!Array.isArray(data) || data.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <div className="d-flex flex-column align-items-center">
                                <div className="avatar avatar-lg rounded bg-label-secondary mb-3">
                                  <i className="ri ri-file-search-line ri-28px" />
                                </div>

                                <h6 className="mb-1">No transactions found</h6>

                                <p className="text-muted mb-0">
                                  Try changing your filters or date range.
                                </p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          data.map((item, index) => {
                            const transactionType = String(
                              item?.type || "",
                            ).toLowerCase();

                            const isCredit = transactionType === "credit";

                            const isDebit = transactionType === "debit";

                            const amount = Number(item?.amount || 0);

                            return (
                              <tr
                                key={item?.id || `${index}-${item?.created_at}`}
                              >
                                {/* Number */}
                                <td className="ps-4">
                                  <span className="text-muted fw-medium">
                                    {(Number(nav?.from) || 1) + index}
                                  </span>
                                </td>

                                {/* Date */}
                                <td>
                                  <div className="fw-medium">
                                    {formatDate(item?.created_at)}
                                  </div>

                                  <small className="text-muted">
                                    {item?.created_at
                                      ? new Date(
                                          item.created_at,
                                        ).toLocaleTimeString("en-IN", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })
                                      : ""}
                                  </small>
                                </td>

                                {/* Payment */}
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className={`avatar avatar-sm rounded ${
                                        isCredit
                                          ? "bg-label-success"
                                          : "bg-label-danger"
                                      }`}
                                    >
                                      <i
                                        className={`ri ${
                                          isCredit
                                            ? "ri-arrow-down-line"
                                            : "ri-arrow-up-line"
                                        }`}
                                      />
                                    </div>

                                    <div>
                                      <div className="fw-semibold text-capitalize">
                                        {item?.payment_method || "N/A"}
                                      </div>

                                      {item?.trans_id ? (
                                        <small className="text-muted">
                                          Ref: {item.trans_id}
                                        </small>
                                      ) : (
                                        <small className="text-muted">
                                          No reference
                                        </small>
                                      )}
                                    </div>
                                  </div>
                                </td>

                                {/* Bank */}
                                <td>
                                  {item?.bank ? (
                                    <div>
                                      <div className="fw-semibold">
                                        {item.bank.account_no || "N/A"}
                                      </div>

                                      <small className="text-muted">
                                        {item.bank.owner_name || "Account"}
                                      </small>
                                    </div>
                                  ) : (
                                    <span className="text-muted">N/A</span>
                                  )}
                                </td>

                                {/* Remark */}
                                <td>
                                  <div
                                    className="text-truncate"
                                    style={{
                                      maxWidth: 220,
                                    }}
                                    title={item?.note || ""}
                                  >
                                    {item?.note || "-"}
                                  </div>
                                </td>

                                {/* Credit */}
                                <td className="text-end">
                                  {isCredit ? (
                                    <span className="fw-bold text-success">
                                      + {formatCurrency(amount)}
                                    </span>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>

                                {/* Debit */}
                                <td className="text-end pe-4">
                                  {isDebit ? (
                                    <span className="fw-bold text-danger">
                                      − {formatCurrency(amount)}
                                    </span>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* -------------------------------------------------------- */}
                  {/* PAGINATION                                               */}
                  {/* -------------------------------------------------------- */}

                  {nav?.total > 0 && (
                    <div className="card-footer border-top">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                        <div className="text-muted small">
                          Showing <strong>{nav?.from || 0}</strong> to{" "}
                          <strong>{nav?.to || 0}</strong> of{" "}
                          <strong>{nav?.total || 0}</strong> transactions
                        </div>

                        <nav aria-label="Transaction pagination">
                          <ul className="pagination pagination-sm mb-0">
                            {/* Previous */}
                            <li
                              className={`page-item ${
                                Number(nav?.current_page) <= 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                type="button"
                                className="page-link"
                                onClick={() => {
                                  if (Number(nav?.current_page) > 1) {
                                    setPage(Number(nav.current_page) - 1);
                                  }
                                }}
                              >
                                <i className="ri ri-arrow-left-s-line" />
                              </button>
                            </li>

                            {paginationPages.map((pageNumber, index) => {
                              const previous = paginationPages[index - 1];

                              const showDots =
                                previous && pageNumber - previous > 1;

                              return (
                                <React.Fragment key={pageNumber}>
                                  {showDots && (
                                    <li className="page-item disabled">
                                      <span className="page-link">…</span>
                                    </li>
                                  )}

                                  <li
                                    className={`page-item ${
                                      Number(nav?.current_page) === pageNumber
                                        ? "active"
                                        : ""
                                    }`}
                                  >
                                    <button
                                      type="button"
                                      className="page-link"
                                      onClick={() => setPage(pageNumber)}
                                    >
                                      {pageNumber}
                                    </button>
                                  </li>
                                </React.Fragment>
                              );
                            })}

                            {/* Next */}
                            <li
                              className={`page-item ${
                                Number(nav?.current_page) >=
                                Number(nav?.last_page || 1)
                                  ? "disabled"
                                  : ""
                              }`}
                            >
                              <button
                                type="button"
                                className="page-link"
                                onClick={() => {
                                  if (
                                    Number(nav?.current_page) <
                                    Number(nav?.last_page || 1)
                                  ) {
                                    setPage(Number(nav.current_page) + 1);
                                  }
                                }}
                              >
                                <i className="ri ri-arrow-right-s-line" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>

                {/* Error */}
                {error && (
                  <div className="alert alert-warning mt-3">
                    <i className="ri ri-error-warning-line me-2" />
                    {error}
                  </div>
                )}
              </div>

              <Footer />

              <div className="content-backdrop fade"></div>
            </div>
          </div>
        </div>

        <div className="layout-overlay layout-menu-toggle"></div>
        <div className="drag-target"></div>
      </div>
    </>
  );
}
